import type { JsonLd } from '@atlaskit/json-ld-types';
import type { SmartLinkResponse } from '@atlaskit/linking-types';
import { getGoalStatusAppearance, getGoalStatusLabel } from '@atlassian/goal-status';
import type { JiraGoalStatus } from './__generated__/goals_issueFieldGoalsReadviewFull_GoalsReadView.graphql';

export const generatePlaceholderData = (
	url: string,
	name?: string | null,
	status?: JiraGoalStatus | null,
): SmartLinkResponse | undefined => {
	// TODO - Remove name and status checks when cleaning goals_ssr_smart_card (ANIP-459)
	if (!url || !name || !status) {
		return undefined;
	}

	// `atlassian:state` is not typed in SmartLinkResponse, so we needed to forcibly set data to JsonLd.Data.Goal
	const data: JsonLd.Data.Goal = {
		'@context': {
			'@vocab': 'https://www.w3.org/ns/activitystreams#',
			atlassian: 'https://schema.atlassian.com/ns/vocabulary#',
			schema: 'http://schema.org/',
		},
		generator: {
			'@type': 'Application',
			'@id': 'https://www.atlassian.com/#TeamCentral',
			name: 'Goals',
		},
		'@type': ['Object', 'atlassian:Goal'],
		url,
		// TODO - Review if we can use real icon via React Node usage (ANIP-361)
		icon: {
			'@type': 'Image',
			url: 'data:image/png;base64,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',
		},
		name,
		'atlassian:state': {
			'@type': 'Object',
			name: getGoalStatusLabel(status).defaultMessage,
			appearance: getGoalStatusAppearance(status),
		},
	};

	return {
		meta: {
			auth: [],
			definitionId: 'watermelon-object-provider',
			product: 'townsquare',
			visibility: 'restricted',
			access: 'granted',
			key: 'watermelon-object-provider',
		},
		data,
	};
};
